/** @jsx jsx */
import React, { useState, useEffect, useContext, useCallback } from "react"
import { useStaticQuery, graphql } from "gatsby"
import { Box, jsx } from "theme-ui"
import { useTransition, config } from "react-spring"

import { FlavorNotesModal } from "../FlavorNotesModal"
import { HowItWorksModal } from "../HowItWorksModal"

import MobileGrid from "./MobileGrid"
import DesktopGrid from "./DesktopGrid"

import { Context } from "../../context/MainContext"
import { ShopifyContext } from "../../context/ShopifyContext"

import {
  formatProductData,
  reduceBundleItems,
  isBrowser,
  lockScroll,
} from "../../util"
import enums from "../../util/enums"
import useEventListener from "../../hooks/useEventListener"

const CaseBuilder = () => {
  const { checkout, setBundleSize } = useContext(ShopifyContext)

  const {
    store: { caseBuilderMode, isMobile },
  } = useContext(Context)

  useEffect(() => {
    function enterBuildMode() {
      if (!isBrowser) return
      if (caseBuilderMode === enums.mode.bundle && !isMobile) {
        lockScroll(true)
        window.scrollTo({
          top: document.getElementById("our-pints").offsetTop,
          left: 0,
          behavior: "smooth",
        })
      }
    }
    enterBuildMode()
  }) //eslint-disable-line

  const handleResize = useCallback(() => {
    if (!isBrowser) return
    if (caseBuilderMode === enums.mode.bundle) {
      window.scrollTo({
        top: document.getElementById("our-pints").offsetTop,
        left: 0,
        behavior: "smooth",
      })
    }
  }, [caseBuilderMode])

  useEventListener("resize", handleResize)

  const data = useStaticQuery(graphql`
    query CaseBuilderQuery {
      sanityCaseBuilder(_id: { eq: "caseBuilder" }) {
        groupShot {
          asset {
            localFile {
              childImageSharp {
                fluid(maxWidth: 2000) {
                  ...GatsbyImageSharpFluid
                }
              }
            }
          }
        }
        groupShotMobile {
          asset {
            localFile(width: 800) {
              publicURL
            }
          }
        }
        activeProducts {
          _id
          shopifyId
          title
          disabled
          description: _rawDescription
          ingredients
          available: sourceData {
            availableForSale
          }
          variants {
            title
            shopifyId: shopifyVariantID
          }
          nutritionFacts {
            asset {
              localFile {
                childImageSharp {
                  fluid(maxWidth: 800) {
                    ...GatsbyImageSharpFluid_noBase64
                  }
                }
              }
            }
          }
          primaryImage {
            asset {
              localFile {
                childImageSharp {
                  fluid(maxWidth: 2000) {
                    ...GatsbyImageSharpFluid
                  }
                }
              }
            }
          }
          mobileImage {
            asset {
              localFile {
                childImageSharp {
                  fluid(maxWidth: 800) {
                    ...GatsbyImageSharpFluid
                  }
                }
              }
            }
          }
          primaryColor {
            hex
          }
          secondaryColor {
            hex
          }
          backgroundColor {
            hex
          }
        }
      }
    }
  `)

  const flavors = formatProductData(
    data.sanityCaseBuilder.activeProducts,
    checkout.products
  )

  const groupShot =
    data.sanityCaseBuilder.groupShot.asset.localFile.childImageSharp.fluid
  const groupShotMobile =
    data.sanityCaseBuilder.groupShotMobile.asset.localFile.publicURL

  const [isOpen, setIsOpen] = useState(false)
  const [index, setIndex] = useState(0)

  const transitions = useTransition(flavors[index], item => item.title, {
    from: { opacity: 0.5 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: config.fast,
  })

  const colors = {
    heading:
      caseBuilderMode === enums.mode.view
        ? flavors[index].primaryColor
        : "#42210B",
    text:
      caseBuilderMode === enums.mode.view
        ? flavors[index].secondaryColor
        : "#42210B",
    background:
      caseBuilderMode === enums.mode.view
        ? flavors[index].secondaryColor
        : "#F7E8A1",
  }

  const styles = {
    section: {
      overflow: "hidden",
      fontFamily: "body",
      position: "relative",
      transition: "padding-top 250ms linear",
      padding: [
        "88px 8px 0px",
        "88px 8px 0px",
        caseBuilderMode === enums.mode.view
          ? "120px 20px 20px"
          : "20px 20px 0px",
      ],
      height: ["100%", "100%", "100vh", null],
      minHeight: [
        null,
        null,
        !isMobile && caseBuilderMode === enums.mode.view ? "800px" : null,
      ],
      width: "100%",
      display: "flex",
      alignItems: "flex-end",
      flexDirection: "column",
      background: `linear-gradient(#D5E0B8 0%, ${colors.background} 22%, #CCC2BA 100%)`,
      fontSize: "50px",
      "@media screen and (max-width: 1500px)": {
        fontSize: "45px",
      },
      "@media screen and (max-width: 1350px)": {
        fontSize: "40px",
      },
      "@media screen and (max-width: 1250px)": {
        fontSize: "36px",
      },
      "@media screen and (max-width: 1100px)": {
        fontSize: "32px",
      },
      "@media screen and (max-height: 1000px)": {
        fontSize: "45px",
      },
      "@media screen and (max-height: 800px) ": {
        fontSize: "38px",
      },
    },
  }

  const numItems = checkout.bundle.reduce(reduceBundleItems, 0)
  const selectedVariants = checkout.bundle.map(item => item.productId)

  return (
    <React.Fragment>
      <FlavorNotesModal flavor={flavors[index]} />
      <HowItWorksModal />

      <Box id="our-pints" as="section" sx={styles.section}>
        <MobileGrid
          backgroundImage={groupShotMobile}
          bundleSize={checkout.bundleSize}
          colors={colors}
          flavors={flavors}
          index={index}
          isOpen={isOpen}
          numItems={numItems}
          selectedVariants={selectedVariants}
          setIndex={setIndex}
          setIsOpen={setIsOpen}
          transitions={transitions}
        />

        <DesktopGrid
          bundleSize={checkout.bundleSize}
          colors={colors}
          flavors={flavors}
          backgroundImageFluid={groupShot}
          index={index}
          isOpen={isOpen}
          numItems={numItems}
          selectedVariants={selectedVariants}
          setBundleSize={setBundleSize}
          setIndex={setIndex}
          setIsOpen={setIsOpen}
          transitions={transitions}
        />
      </Box>
    </React.Fragment>
  )
}

export default CaseBuilder
